import { DateRange } from 'bold-ui'
import { useAlert } from 'components/alert'
import { TipoAgrupamentoRelatorioGerencialEnum } from 'graphql/types.generated'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { convertDateRangeToLocalDateRange } from 'util/date/dateRange'

import { Aggregator } from '../components/aggregator/model'
import { KeyMap } from '../components/model'
import { Dictionary, OperationsPaths, TreeMeta } from './model-treeBuilder'
import { MetaResult, TreeBuilderStatus } from './model-useTreeBuilder'
import { TreeBuilder } from './TreeBuilder'

interface TreeBuilderHookProps<T extends object> {
  keyMapping: KeyMap<T>
  operations?: OperationsPaths
  data?: ReadonlyArray<T>
}

export interface TreeBuilderHookResult<T extends object> {
  metaState: TreeMeta<T> | undefined
  maxLeafValue: number | undefined
  defaultTree: Dictionary<T> | undefined
  complementaryTree: Dictionary<T> | undefined
  treeBuilderStatus: TreeBuilderStatus
  isMetaEmpty: boolean
  tipoAgrupamento: TipoAgrupamentoRelatorioGerencialEnum | undefined
  setDateFilter: (filter: DateRange | undefined | null, periodUnit: string) => void
  setCiapCidFilter: (filter: string[]) => void
  setTipoAgrupamento: (tipoAgrupamento: TipoAgrupamentoRelatorioGerencialEnum) => void
  setIsSearchingMeta: (isSearchingMeta: boolean) => void
  build: <K extends keyof T>(
    rowKeys: Array<K>,
    columnKeys: Array<K>,
    aggregator: Aggregator,
    aggregatorKey: keyof T,
    filterKeys?: Map<K, Set<string>>
  ) => void
  meta: (
    period: DateRange | null | undefined,
    unit: string,
    ciapCid?: string[],
    tipoAgrupamento?: TipoAgrupamentoRelatorioGerencialEnum | null
  ) => MetaResult<T>
  demolish: () => void
}

export function useTreeBuilder<T extends object>(props: TreeBuilderHookProps<T>): TreeBuilderHookResult<T> {
  const { keyMapping, operations, data } = props

  const [metaState, setMetaState] = useState<TreeMeta<T>>()

  const [defaultTreeState, setDefaulTreeState] = useState<Dictionary<T>>()

  const [complementaryTreeState, setComplementaryTreeState] = useState<Dictionary<T>>()

  const [maxLeafValueState, setMaxLeafValueState] = useState<number>()

  const [dateRange, setDateRange] = useState<DateRange | null>()

  const [periodUnit, setPeriodUnit] = useState<string>()

  const [ciapCidFilter, setCiapCidFilter] = useState<string[]>()

  const [tipoAgrupamento, setTipoAgrupamento] = useState<TipoAgrupamentoRelatorioGerencialEnum>()

  const treeBuilder = useMemo<TreeBuilder<T>>(() => new TreeBuilder(data && [...data], operations), [data, operations])

  const [treeBuilderStatus, setTreeBuilderStatus] = useState<TreeBuilderStatus>({
    isBuilding: false,
    isSearchingMeta: false,
  })

  const setIsBuilding = (isBuilding: boolean) => setTreeBuilderStatus((prevStatus) => ({ ...prevStatus, isBuilding }))

  const setIsSearchingMeta = (isSearchingMeta: boolean) =>
    setTreeBuilderStatus((prevStatus) => ({ ...prevStatus, isSearchingMeta }))

  const alert = useAlert()

  const warning = useCallback((msg: any) => alert('danger', msg.error), [alert])

  const setDateFilter = useCallback((filter: DateRange | undefined | null, periodUnit: string) => {
    setDateRange(filter)
    setPeriodUnit(periodUnit)
  }, [])

  const demolish = useCallback(() => {
    setComplementaryTreeState(undefined)
    setDefaulTreeState(undefined)
  }, [])

  const build = useCallback(
    function <K extends keyof T>(
      rowKeys: Array<K>,
      columnKeys: Array<K>,
      aggregator: Aggregator,
      aggregatorKey: keyof T,
      filterKeys?: Map<K, Set<string>>
    ) {
      setIsBuilding(true)
      const localDateRange = convertDateRangeToLocalDateRange(dateRange)
      if (rowKeys.length > 0 && columnKeys.length > 0) {
        treeBuilder
          .build(
            [...columnKeys, ...rowKeys],
            localDateRange,
            periodUnit,
            ciapCidFilter,
            tipoAgrupamento,
            aggregator,
            aggregatorKey,
            filterKeys
          )
          .then((data) => {
            setComplementaryTreeState(data)
            setIsBuilding(false)
          })
          .catch(warning)
      }
      treeBuilder
        .build(
          [...rowKeys, ...columnKeys],
          localDateRange,
          periodUnit,
          ciapCidFilter,
          tipoAgrupamento,
          aggregator,
          aggregatorKey,
          filterKeys
        )
        .then((data) => {
          setDefaulTreeState(data)
          setMaxLeafValueState(treeBuilder.maxLeafValue)
          setIsBuilding(false)
        })
        .catch(warning)
    },
    [dateRange, treeBuilder, periodUnit, ciapCidFilter, tipoAgrupamento, warning]
  )

  const meta = useCallback(
    async (
      period: DateRange | null | undefined,
      unit: string,
      ciapCid: string[] = [],
      tipoAgrupamento: TipoAgrupamentoRelatorioGerencialEnum | null = null
    ): MetaResult<T> => {
      const localDateRange = convertDateRangeToLocalDateRange(period)
      setMetaState(undefined)
      setIsSearchingMeta(true)
      return treeBuilder
        .meta(keyMapping, localDateRange, unit, ciapCid, tipoAgrupamento)
        .then((data) => {
          if (data) {
            setMetaState(data)
            setIsSearchingMeta(false)
          }
          return data
        })
        .catch((message) => {
          warning(message)
          return undefined
        })
    },
    [treeBuilder, keyMapping, warning]
  )

  // Tratando o cenario do absenteismo que usa o GraphQL para trazer os dados
  useEffect(() => {
    if (!isUndefinedOrNull(data)) meta(dateRange, periodUnit, ciapCidFilter, tipoAgrupamento)
  }, [ciapCidFilter, data, dateRange, meta, periodUnit, tipoAgrupamento])

  return {
    metaState,
    maxLeafValue: maxLeafValueState,
    defaultTree: defaultTreeState,
    complementaryTree: complementaryTreeState,
    treeBuilderStatus,
    isMetaEmpty: metaState?.isEmpty ?? false,
    tipoAgrupamento,
    setDateFilter,
    setCiapCidFilter,
    setTipoAgrupamento,
    setIsSearchingMeta,
    build,
    meta,
    demolish,
  }
}
